// src/pages/Admin/Dashboard/Dashboard.tsx

import React, { useState } from 'react';
import { Container, Grid, Box, CircularProgress, Typography, Alert, Stack, Button, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Notifications, TrendingUp } from '@mui/icons-material';
import { AlertTitle } from '@mui/material';

// Import custom hook and components
import { useDashboardData } from './hooks/useDashboardData';
import { useAuth } from '../../../contexts/AuthContext';
import MetricsCards from './components/MetricsCards';
import RecentDonors from './components/RecentDonors';
import RecentActivities from './components/RecentActivities';
import UpcomingEvents from './components/UpcomingEvents';
import AdditionalStats from './components/AdditionalStats';
import { PerformanceGoals } from './components/PerformanceGoals';
import MainContent from './components/MainContent';

// Import modal components
import DonorsModal from '../../../components/Admin/Dashboard/DonorsModal';
import DonationsModal from '../../../components/Admin/Dashboard/DonationsModal';
import VolunteersModal from '../../../components/Admin/Dashboard/VolunteersModal';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { tokens } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const [donorsModalOpen, setDonorsModalOpen] = useState(false);
  const [donationsModalOpen, setDonationsModalOpen] = useState(false);
  const [volunteersModalOpen, setVolunteersModalOpen] = useState(false);
  
  // Load dashboard data
  const {
    metrics,
    recentDonors,
    recentDonorDonations,
    recentDonations,
    recentVolunteers,
    upcomingEvents,
    combinedActivities,
    performanceGoals,
    donationGrowth,
    donorGrowth,
    volunteerGrowth,
    isLoading,
    error
  } = useDashboardData();
  
  // Handler functions for "View All" buttons - these can either navigate or open modals
  const handleViewAllDonors = () => setDonorsModalOpen(true);
  const handleViewAllDonations = () => setDonationsModalOpen(true);
  const handleViewAllVolunteers = () => setVolunteersModalOpen(true);
  
  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };
  
  // Modal toggle handlers
  const handleOpenDonationsModal = () => setDonationsModalOpen(true);
  const handleCloseDonationsModal = () => setDonationsModalOpen(false);
  const handleOpenDonorsModal = () => setDonorsModalOpen(true);
  const handleCloseDonorsModal = () => setDonorsModalOpen(false);
  const handleOpenVolunteersModal = () => setVolunteersModalOpen(true);
  const handleCloseVolunteersModal = () => setVolunteersModalOpen(false);

  // Show loading state when data is being fetched
  if (isLoading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="80vh"
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  // Show error state if any
  if (error) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="80vh"
        sx={{ p: 2 }}
      >
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Typography variant="body1">
          Please try refreshing the page or contact support if the problem persists.
        </Typography>
        <Button 
          variant="contained" 
          sx={{ mt: 3 }}
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </Button>
      </Box>
    );
  }

  // Log stats for debugging
    // console.log("Dashboard render stats:", {
    //   hasMetrics: !!metrics,
    //   uniqueDonorCount: recentDonors?.length,
    //   donorDonationsCount: recentDonorDonations?.length,
    //   donationCount: recentDonations?.length
    // });

  return (
    <Box 
      component="section"
      sx={{ 
        p: { xs: 2, md: 3 }, 
        backgroundColor: theme.palette.background.default, 
        minHeight: "100vh" 
      }}
    >
      {/* Dashboard Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}
      >
        <Box>
          <Typography 
            component="h1"
            variant="h4" 
            sx={{ fontWeight: 700, mb: 0.5 }}
          >
            Admin Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Welcome back! Here's what's happening with your organization.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button 
            variant="outlined" 
            startIcon={<Notifications />}
            sx={{ borderRadius: 2 }}
          >
            Notifications
          </Button>
          <Button 
            variant="contained" 
            startIcon={<TrendingUp />}
            sx={{ borderRadius: 2 }}
          >
            Generate Report
          </Button>
        </Stack>
      </Box>

      {/* Metrics Cards */}
      <MetricsCards 
        metrics={metrics} 
        donationGrowth={donationGrowth}
        donorGrowth={donorGrowth}
        volunteerGrowth={volunteerGrowth}
        onOpenDonationsModal={handleOpenDonationsModal}
        onOpenDonorsModal={handleOpenDonorsModal}
        onOpenVolunteersModal={handleOpenVolunteersModal}
      />

      <Grid container spacing={3}>
        {/* Left Section */}
        <Grid item xs={12} lg={8}>
          {/* Tabs for main content */}
          <MainContent 
            currentTab={currentTab}
            onTabChange={handleTabChange}
            metrics={metrics}
            performanceGoals={performanceGoals || []}
            activityItems={combinedActivities || []}
          />

          {/* Additional Stats */}
          <AdditionalStats metrics={metrics} />
        </Grid>
        
        {/* Right Section */}
        <Grid item xs={12} lg={4}>
          {/* Recent Donors */}
          <RecentDonors
            recentDonors={recentDonors || []}
            onViewAll={handleOpenDonationsModal}
          />
          
          {/* Upcoming Events */}
          <UpcomingEvents upcomingEvents={upcomingEvents || []} />
        </Grid>
      </Grid>

      {/* Modals */}
      {tokens?.idToken && (
        <>
          <DonorsModal 
            open={donorsModalOpen} 
            onClose={handleCloseDonorsModal} 
            token={tokens.idToken} 
          />
          <DonationsModal 
            open={donationsModalOpen} 
            onClose={handleCloseDonationsModal} 
          />
          <VolunteersModal 
            open={volunteersModalOpen} 
            onClose={handleCloseVolunteersModal} 
            token={tokens.accessToken || tokens.idToken} 
          />
        </>
      )}
    </Box>
  );
};

export default Dashboard;
